import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Users, 
  Loader, 
  ArrowRight, 
  Coffee,
  Recycle,
  Leaf,
  MapPin,
  Sprout,
  Brain,
  Zap,
  Globe,
  ChevronRight,
  Cloud,
  TrendingUp,
  Droplets
} from 'lucide-react';
import { signInAsDemo } from '../lib/auth';
import AnimatedSection from '../components/AnimatedSection';
import { motion } from 'framer-motion';

const EdwardsHamletDemo: React.FC = () => {
  const navigate = useNavigate(); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'impact' | 'technology'>('overview');

  const handleDemoAccess = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await signInAsDemo();
      
      if (result.success) {
        navigate('/dashboard'); 
      } else {
        setError(result.error || 'Failed to access demo account');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <div className="relative h-[50vh]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1597916829826-02e5bb4a54e0"
            alt="Coffee farmer in Uganda"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-transparent" />
        </div>
        <div className="absolute inset-0">
          <div className="container mx-auto px-4 h-full flex items-center">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white mb-4">
                <MapPin className="h-5 w-5" />
                <span>Jinja, Uganda</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                Edward's Hamlet Regeneration Centre
              </h1>
              <p className="text-xl text-white/90 mb-6">
                Our flagship implementation showcasing AVN technology with 273 community farmers growing regenerative coffee and developing innovative value-added products.
              </p>
              <button
                onClick={handleDemoAccess}
                disabled={loading}
                className="bg-white text-avn-purple-700 px-6 py-3 rounded-lg hover:bg-avn-purple-50 transition-colors inline-flex items-center gap-2 text-lg"
              >
                {loading ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    <span>Accessing...</span>
                  </>
                ) : (
                  <>
                    <span>Access Edward's Dashboard</span>
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Error Message */}
        {error && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-red-50 text-red-700 p-6 rounded-lg">
              <h2 className="text-xl font-bold mb-2">Error Accessing Demo</h2>
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center gap-2 px-4 py-2 text-lg font-medium border-b-2 transition-colors ${
                activeTab === 'overview' 
                  ? 'border-avn-purple-600 text-avn-purple-700' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Building2 className="h-5 w-5" />
              <span>Overview</span>
            </button>
            <button
              onClick={() => setActiveTab('impact')}
              className={`flex items-center gap-2 px-4 py-2 text-lg font-medium border-b-2 transition-colors ${
                activeTab === 'impact' 
                  ? 'border-avn-purple-600 text-avn-purple-700' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <TrendingUp className="h-5 w-5" />
              <span>Impact</span>
            </button>
            <button
              onClick={() => setActiveTab('technology')}
              className={`flex items-center gap-2 px-4 py-2 text-lg font-medium border-b-2 transition-colors ${
                activeTab === 'technology' 
                  ? 'border-avn-purple-600 text-avn-purple-700' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Brain className="h-5 w-5" />
              <span>Technology</span>
            </button>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'overview' && (
            <div>
              <AnimatedSection>
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Building2 className="h-6 w-6 text-avn-purple-600" />
                    <span>About Edward's Hamlet</span>
                  </h2>
                  
                  <p className="text-gray-700 mb-6">
                    Edward's Hamlet is AVN's flagship regeneration centre in Jinja, Uganda, showcasing our complete agricultural ecosystem in action. This 12-acre farm functions as both a demonstration site and a community hub for 273 smallholder farmers who grow premium coffee and develop innovative value-added products.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="font-semibold text-xl mb-4">Key Features</h3>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-avn-purple-100 rounded-full mt-1">
                            <Coffee className="h-5 w-5 text-avn-purple-600" />
                          </div>
                          <div>
                            <span className="font-medium">Unity Bean Coffee</span>
                            <p className="text-gray-600 text-sm">Premium coffee grown through regenerative practices with full supply chain transparency.</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-green-100 rounded-full mt-1">
                            <Recycle className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <span className="font-medium">Value-Added Processing</span>
                            <p className="text-gray-600 text-sm">Coffee cherry syrup and other byproducts create additional revenue streams.</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-blue-100 rounded-full mt-1">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <span className="font-medium">Community Hub</span>
                            <p className="text-gray-600 text-sm">Serving 273 local farmers with training, technology access, and market connections.</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-amber-100 rounded-full mt-1">
                            <Leaf className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <span className="font-medium">Agroforestry Systems</span>
                            <p className="text-gray-600 text-sm">Integrating trees, crops, and beneficial plants for resilient ecosystems.</p>
                          </div>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <img 
                        src="https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e" 
                        alt="Agroforestry farming" 
                        className="rounded-lg shadow-md w-full h-64 object-cover mb-4"
                      />
                      <p className="text-sm text-gray-500 italic">Edward's Hamlet uses agroforestry techniques to create resilient farming systems.</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.1}>
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Users className="h-6 w-6 text-avn-purple-600" />
                    <span>Farmer Engagement Model</span>
                  </h2>
                  
                  <div className="space-y-6">
                    <p className="text-gray-700">
                      Our business model is built around transparency, fair compensation, and long-term sustainability. Here's how farmers engage with Edward's Hamlet:
                    </p>
                    
                    <div className="relative">
                      {/* Timeline Line */}
                      <div className="absolute left-16 top-0 bottom-0 w-1 bg-gray-200"></div>
                      
                      {/* Timeline Items */}
                      <div className="space-y-8">
                        <div className="flex gap-6">
                          <div className="flex-shrink-0 w-16 h-16 bg-avn-purple-100 rounded-full flex items-center justify-center z-10">
                            <Users className="h-8 w-8 text-avn-purple-600" />
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4 flex-grow">
                            <h3 className="font-bold text-lg mb-2">1. Join the Network</h3>
                            <p className="text-gray-600">
                              Farmers join the AVN network and receive training in regenerative farming practices at Edward's Hamlet.
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex gap-6">
                          <div className="flex-shrink-0 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center z-10">
                            <Sprout className="h-8 w-8 text-green-600" />
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4 flex-grow">
                            <h3 className="font-bold text-lg mb-2">2. Implementation</h3>
                            <p className="text-gray-600">
                              Equipped with knowledge and support, farmers implement regenerative practices on their own land.
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex gap-6">
                          <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center z-10">
                            <Coffee className="h-8 w-8 text-blue-600" />
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4 flex-grow">
                            <h3 className="font-bold text-lg mb-2">3. Processing & Value Addition</h3>
                            <p className="text-gray-600">
                              Harvests are processed at Edward's Hamlet, where value-added products like coffee cherry syrup are developed.
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex gap-6">
                          <div className="flex-shrink-0 w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center z-10">
                            <Globe className="h-8 w-8 text-amber-600" />
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4 flex-grow">
                            <h3 className="font-bold text-lg mb-2">4. Market Access</h3>
                            <p className="text-gray-600">
                              Products are marketed globally with full transparency and traceability, securing premium prices.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
              
              <AnimatedSection delay={0.2}>
                <div className="bg-avn-purple-50 rounded-xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Zap className="h-6 w-6 text-avn-purple-600" />
                    <span>Payment Structure</span>
                  </h2>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="font-semibold text-xl mb-4">Profit Distribution</h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="font-medium">Farmer's Share</span>
                            <span className="font-bold">25-50%</span>
                          </div>
                          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-green-500" style={{ width: '25%' }}></div>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">Guaranteed minimum 25% with potential to earn up to 50%</p>
                        </div>
                        
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="font-medium">AVN Operations</span>
                            <span className="font-bold">20%</span>
                          </div>
                          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-avn-purple-500" style={{ width: '20%' }}></div>
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="font-medium">Cooperative Management</span>
                            <span className="font-bold">5%</span>
                          </div>
                          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500" style={{ width: '5%' }}></div>
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="font-medium">Training & Capacity</span>
                            <span className="font-bold">5%</span>
                          </div>
                          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500" style={{ width: '5%' }}></div>
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="font-medium">Platform Development</span>
                            <span className="font-bold">20%</span>
                          </div>
                          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500" style={{ width: '20%' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-xl mb-4">Payment Timeline</h3>
                      <ul className="space-y-4">
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-avn-purple-100 rounded-full mt-1">
                            <Zap className="h-5 w-5 text-avn-purple-600" />
                          </div>
                          <div>
                            <span className="font-medium">Immediate Payment</span>
                            <p className="text-gray-600 text-sm">15% of farmer's share paid immediately upon delivery of produce.</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-green-100 rounded-full mt-1">
                            <TrendingUp className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <span className="font-medium">30-Day Final Payment</span>
                            <p className="text-gray-600 text-sm">Remaining 85% of farmer's share paid within 30 days of delivery.</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-blue-100 rounded-full mt-1">
                            <Globe className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <span className="font-medium">Premium Markets</span>
                            <p className="text-gray-600 text-sm">Access to premium markets secured through AVN's verified ethical supply chain.</p>
                          </div>
                        </li>
                      </ul>
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-500">
                          <strong>Note:</strong> Platform sales incur an additional 3% fee to cover logistics and market access costs.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          )}

          {activeTab === 'impact' && (
            <div>
              <AnimatedSection>
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <TrendingUp className="h-6 w-6 text-avn-purple-600" />
                    <span>Impact Metrics</span>
                  </h2>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-gray-50 p-6 rounded-xl text-center">
                      <div className="text-3xl font-bold text-avn-purple-600 mb-2">273</div>
                      <div className="text-gray-600">Farmers Connected</div>
                    </div>
                    <div className="bg-gray-50 p-6 rounded-xl text-center">
                      <div className="text-3xl font-bold text-green-600 mb-2">45%</div>
                      <div className="text-gray-600">Yield Increase</div>
                    </div>
                    <div className="bg-gray-50 p-6 rounded-xl text-center">
                      <div className="text-3xl font-bold text-blue-600 mb-2">30%</div>
                      <div className="text-gray-600">Water Savings</div>
                    </div>
                    <div className="bg-gray-50 p-6 rounded-xl text-center">
                      <div className="text-3xl font-bold text-amber-600 mb-2">25-50%</div>
                      <div className="text-gray-600">Revenue Share</div>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="font-semibold text-xl mb-4">Environmental Impact</h3>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-green-100 rounded-full mt-1">
                            <Leaf className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <span className="font-medium">Carbon Sequestration</span>
                            <p className="text-gray-600 text-sm">Regenerative practices sequester 5 tons of carbon per hectare annually.</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-blue-100 rounded-full mt-1">
                            <Droplets className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <span className="font-medium">Water Conservation</span>
                            <p className="text-gray-600 text-sm">Smart irrigation and soil management reduce water usage by 30%.</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-amber-100 rounded-full mt-1">
                            <Sprout className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <span className="font-medium">Biodiversity</span>
                            <p className="text-gray-600 text-sm">Increased plant diversity by 40% and beneficial insects by 60%.</p>
                          </div>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-xl mb-4">Social Impact</h3>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-avn-purple-100 rounded-full mt-1">
                            <Users className="h-5 w-5 text-avn-purple-600" />
                          </div>
                          <div>
                            <span className="font-medium">Community Empowerment</span>
                            <p className="text-gray-600 text-sm">Skills development and technology transfer to 273 local farmers.</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-blue-100 rounded-full mt-1">
                            <Globe className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <span className="font-medium">Global Market Access</span>
                            <p className="text-gray-600 text-sm">Direct connections to premium markets in Europe and North America.</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="p-2 bg-green-100 rounded-full mt-1">
                            <TrendingUp className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <span className="font-medium">Income Security</span>
                            <p className="text-gray-600 text-sm">Farmers have seen a 35% increase in stable income through diversification.</p>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.1}>
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Coffee className="h-6 w-6 text-avn-purple-600" />
                    <span>Unity Bean Coffee</span>
                  </h2>
                  
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <img 
                        src="https://images.unsplash.com/photo-1495474472287-4d71bcdd2085" 
                        alt="Coffee farmers sorting beans" 
                        className="rounded-lg shadow-md w-full h-64 object-cover"
                      />
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-xl mb-4">From Farm to Cup</h3>
                      <p className="text-gray-600 mb-4">
                        Unity Bean Coffee is our premium product grown by the Edward's Hamlet cooperative. Every step of the journey is verified through our AVN-Earth AI traceability system.
                      </p>
                      
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2 text-gray-700">
                          <ChevronRight className="h-4 w-4 text-avn-purple-600 flex-shrink-0" />
                          <span>Regeneratively grown, shade-cultivated Arabica</span>
                        </li>
                        <li className="flex items-center gap-2 text-gray-700">
                          <ChevronRight className="h-4 w-4 text-avn-purple-600 flex-shrink-0" />
                          <span>Processed with solar-powered equipment</span>
                        </li>
                        <li className="flex items-center gap-2 text-gray-700">
                          <ChevronRight className="h-4 w-4 text-avn-purple-600 flex-shrink-0" />
                          <span>QR code links to full provenance data</span>
                        </li>
                        <li className="flex items-center gap-2 text-gray-700">
                          <ChevronRight className="h-4 w-4 text-avn-purple-600 flex-shrink-0" />
                          <span>Farmers receive 25-50% of final product value</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.2}>
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Recycle className="h-6 w-6 text-avn-purple-600" />
                    <span>Circular Innovations</span>
                  </h2>
                  
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-green-50 rounded-lg p-6">
                      <div className="p-3 bg-green-100 rounded-full w-fit mb-4">
                        <Coffee className="h-6 w-6 text-green-600" />
                      </div>
                      <h3 className="font-semibold text-lg mb-2">Coffee Cherry Syrup</h3>
                      <p className="text-gray-600 text-sm">
                        Transforming coffee cherries from waste into a valuable natural sweetener, creating new revenue streams.
                      </p>
                      <motion.div 
                        className="mt-4 w-full bg-green-200 h-2 rounded-full overflow-hidden"
                        initial={{ opacity: 0, width: 0 }}
                        whileInView={{ opacity: 1, width: "100%" }}
                        transition={{ delay: 0.5, duration: 0.8 }}
                        viewport={{ once: true }}
                      >
                        <div className="bg-green-600 h-full w-full"></div>
                      </motion.div>
                      <div className="mt-2 text-right text-xs text-green-700">95% waste reduction</div>
                    </div>
                    
                    <div className="bg-amber-50 rounded-lg p-6">
                      <div className="p-3 bg-amber-100 rounded-full w-fit mb-4">
                        <Recycle className="h-6 w-6 text-amber-600" />
                      </div>
                      <h3 className="font-semibold text-lg mb-2">Biochar Production</h3>
                      <p className="text-gray-600 text-sm">
                        Converting agricultural waste into biochar for soil improvement and carbon sequestration.
                      </p>
                      <motion.div 
                        className="mt-4 w-full bg-amber-200 h-2 rounded-full overflow-hidden"
                        initial={{ opacity: 0, width: 0 }}
                        whileInView={{ opacity: 1, width: "100%" }}
                        transition={{ delay: 0.6, duration: 0.8 }}
                        viewport={{ once: true }}
                      >
                        <div className="bg-amber-600 h-full w-3/4"></div>
                      </motion.div>
                      <div className="mt-2 text-right text-xs text-amber-700">75% carbon retention</div>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-6">
                      <div className="p-3 bg-blue-100 rounded-full w-fit mb-4">
                        <Droplets className="h-6 w-6 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-lg mb-2">Water Recycling</h3>
                      <p className="text-gray-600 text-sm">
                        Capturing, filtering, and reusing processing water for irrigation and livestock.
                      </p>
                      <motion.div 
                        className="mt-4 w-full bg-blue-200 h-2 rounded-full overflow-hidden"
                        initial={{ opacity: 0, width: 0 }}
                        whileInView={{ opacity: 1, width: "100%" }}
                        transition={{ delay: 0.7, duration: 0.8 }}
                        viewport={{ once: true }}
                      >
                        <div className="bg-blue-600 h-full w-4/5"></div>
                      </motion.div>
                      <div className="mt-2 text-right text-xs text-blue-700">80% water recapture</div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          )}

          {activeTab === 'technology' && (
            <div>
              <AnimatedSection>
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Brain className="h-6 w-6 text-avn-purple-600" />
                    <span>AVN-Earth AI Technology</span>
                  </h2>
                  
                  <div className="mb-6">
                    <p className="text-gray-700">
                      At Edward's Hamlet, we've implemented our full suite of AVN-Earth AI technology to create a transparent, ethical, and regenerative agricultural system. Here's how our technology stack creates value:
                    </p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div className="bg-avn-purple-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Brain className="h-6 w-6 text-avn-purple-600" />
                          <h3 className="font-semibold text-lg">TraceIt-AI</h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">
                          Our proprietary AI system that verifies ethical practices and creates transparent supply chains from farm to consumer.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-center gap-2 text-gray-700 text-sm">
                            <ChevronRight className="h-4 w-4 text-avn-purple-600 flex-shrink-0" />
                            <span>Blockchain-verified transactions</span>
                          </li>
                          <li className="flex items-center gap-2 text-gray-700 text-sm">
                            <ChevronRight className="h-4 w-4 text-avn-purple-600 flex-shrink-0" />
                            <span>QR code scanning for consumer transparency</span>
                          </li>
                          <li className="flex items-center gap-2 text-gray-700 text-sm">
                            <ChevronRight className="h-4 w-4 text-avn-purple-600 flex-shrink-0" />
                            <span>Impact metric tracking and verification</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Cloud className="h-6 w-6 text-green-600" />
                          <h3 className="font-semibold text-lg">MicroForecast</h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">
                          Hyperlocal weather predictions and farming recommendations customized to each farmer's specific location.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-center gap-2 text-gray-700 text-sm">
                            <ChevronRight className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <span>72-hour precision forecasts</span>
                          </li>
                          <li className="flex items-center gap-2 text-gray-700 text-sm">
                            <ChevronRight className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <span>Farm-specific recommendations</span>
                          </li>
                          <li className="flex items-center gap-2 text-gray-700 text-sm">
                            <ChevronRight className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <span>SMS alerts for critical weather events</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Globe className="h-6 w-6 text-blue-600" />
                          <h3 className="font-semibold text-lg">Market Intelligence</h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">
                          Real-time market data and AI-powered pricing analytics to ensure farmers receive fair compensation.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-center gap-2 text-gray-700 text-sm">
                            <ChevronRight className="h-4 w-4 text-blue-600 flex-shrink-0" />
                            <span>Live global price monitoring</span>
                          </li>
                          <li className="flex items-center gap-2 text-gray-700 text-sm">
                            <ChevronRight className="h-4 w-4 text-blue-600 flex-shrink-0" />
                            <span>Predictive price trend analysis</span>
                          </li>
                          <li className="flex items-center gap-2 text-gray-700 text-sm">
                            <ChevronRight className="h-4 w-4 text-blue-600 flex-shrink-0" />
                            <span>Direct buyer-farmer connections</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="bg-amber-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap className="h-6 w-6 text-amber-600" />
                          <h3 className="font-semibold text-lg">Innovation Lab</h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">
                          Our R&D center developing new value-added products and processing techniques from agricultural byproducts.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-center gap-2 text-gray-700 text-sm">
                            <ChevronRight className="h-4 w-4 text-amber-600 flex-shrink-0" />
                            <span>Coffee cherry syrup development</span>
                          </li>
                          <li className="flex items-center gap-2 text-gray-700 text-sm">
                            <ChevronRight className="h-4 w-4 text-amber-600 flex-shrink-0" />
                            <span>Upcycled packaging materials</span>
                          </li>
                          <li className="flex items-center gap-2 text-gray-700 text-sm">
                            <ChevronRight className="h-4 w-4 text-amber-600 flex-shrink-0" />
                            <span>Biochar and organic fertilizers</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.1}>
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <Leaf className="h-6 w-6 text-avn-purple-600" />
                    <span>Regenerative Farming Systems</span>
                  </h2>
                  
                  <div className="grid md:grid-cols-2 gap-8 mb-8 items-center">
                    <div>
                      <h3 className="font-semibold text-xl mb-4">Hybrid Farming Approach</h3>
                      <p className="text-gray-600 mb-4">
                        Edward's Hamlet combines traditional knowledge with modern technology to create resilient, productive, and ethical farming systems:
                      </p>
                      
                      <ul className="space-y-3">
                        <li className="flex items-center gap-2 text-gray-700">
                          <ChevronRight className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span>Agroforestry systems with multi-layer cropping</span>
                        </li>
                        <li className="flex items-center gap-2 text-gray-700">
                          <ChevronRight className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span>IoT sensors for soil health and moisture monitoring</span>
                        </li>
                        <li className="flex items-center gap-2 text-gray-700">
                          <ChevronRight className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span>Natural pest management through biodiversity</span>
                        </li>
                        <li className="flex items-center gap-2 text-gray-700">
                          <ChevronRight className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span>Water harvesting and precision irrigation</span>
                        </li>
                        <li className="flex items-center gap-2 text-gray-700">
                          <ChevronRight className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span>Biochar and compost for soil carbon sequestration</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <img 
                        src="https://images.unsplash.com/photo-1630690503304-8b1ff196b363" 
                        alt="Regenerative farming system" 
                        className="rounded-lg shadow-md w-full h-64 object-cover"
                      />
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="font-semibold text-lg mb-4">Data-Driven Agriculture</h3>
                    <p className="text-gray-600 mb-4">
                      Edward's Hamlet collects and analyzes data across the entire farming ecosystem to optimize operations and measure impact:
                    </p>
                    
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-white p-4 rounded shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <Cloud className="h-5 w-5 text-blue-600" />
                          <h4 className="font-medium">Climate Data</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Hyperlocal weather patterns, microclimate monitoring, and seasonal trend analysis.
                        </p>
                      </div>
                      
                      <div className="bg-white p-4 rounded shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <Sprout className="h-5 w-5 text-green-600" />
                          <h4 className="font-medium">Crop Performance</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Growth rates, yield metrics, quality indicators, and variety comparisons.
                        </p>
                      </div>
                      
                      <div className="bg-white p-4 rounded shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="h-5 w-5 text-avn-purple-600" />
                          <h4 className="font-medium">Impact Metrics</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Carbon sequestration, biodiversity indices, water efficiency, and economic outcomes.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          )}

          {/* Access Demo Section */}
          <AnimatedSection delay={0.3}>
            <div className="bg-avn-purple-900 text-white rounded-xl p-8 mt-8">
              <h2 className="text-2xl font-bold mb-4">Experience Edward's Hamlet Dashboard</h2>
              <p className="mb-6">
                Access our interactive demo dashboard to explore real-time data, analytics, and insights from Edward's Hamlet in Uganda.
              </p>
              <button
                onClick={handleDemoAccess}
                disabled={loading}
                className="bg-white text-avn-purple-800 px-6 py-3 rounded-lg hover:bg-avn-purple-50 transition-colors inline-flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    <span>Accessing...</span>
                  </>
                ) : (
                  <>
                    <span>Access Demo Dashboard</span>
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </div>
  );
};

export default EdwardsHamletDemo;